import { Component, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 화면 하나가 그리다 죽어도 앱 전체가 빈 화면이 되지 않게 막는 오류 경계 (2026-10-09)
 *
 * 왜 필요한가
 * React 는 화면을 그리다 예외가 나면 그 위에 오류 경계가 없을 때 앱 전체를 지워 버린다.
 * 그러면 사용자에게는 «검은 화면» 만 보이고, 무엇이 왜 죽었는지 아무도 알 수 없다
 * (베트남 KOL 팀이 배송 상세에서 검은 화면을 며칠 겪었는데 서버 로그에는 오류가 없었다 — 서버는 정상 응답을 줬다).
 *
 * 이제 죽으면 «문제가 생겼습니다» 와 함께 **오류 내용을 복사하는 버튼**을 보여 준다.
 * 사용자는 그 내용을 Slack 에 붙이면 되고, 우리는 캡처 없이 원인을 찾는다.
 *
 * - 다른 화면으로 이동하면(주소가 바뀌면) 자동으로 풀린다 — 한 화면의 오류가 앱 전체에 남지 않는다.
 * - 복사 내용에는 주소·역할·시각·오류 문구·호출 위치만 넣는다. 토큰·이메일·화면 안의 값은 넣지 않는다.
 * - 영어 번역(Weglot)이 오류 상세를 바꾸지 않게 data-wg-notranslate 를 붙인다.
 */

const clip = (s, n) => (typeof s === 'string' && s.length > n ? `${s.slice(0, n)}…` : s || '');

export const buildErrorReport = (error, componentStack, location = {}) => {
  let role = '';
  try { role = `${localStorage.getItem('serviceType') || ''}/${localStorage.getItem('adminRole') || ''}`; } catch (e) { /* 무시 */ }
  return [
    `time: ${new Date().toISOString()}`,
    `path: ${location.pathname || ''}${location.search || ''}`,
    `role: ${role}`,
    `error: ${error?.name || 'Error'}: ${clip(String(error?.message || error || ''), 500)}`,
    `stack:\n${clip(String(error?.stack || '').split('\n').slice(0, 8).join('\n'), 1200)}`,
    `component:\n${clip(String(componentStack || '').split('\n').slice(0, 10).join('\n'), 1200)}`,
    `ua: ${clip(typeof navigator !== 'undefined' ? navigator.userAgent : '', 200)}`,
  ].join('\n');
};

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e2) {
      return false;
    }
  }
}

function ErrorScreen({ error, componentStack, location }) {
  const [copied, setCopied] = useState(false);
  const report = buildErrorReport(error, componentStack, location);
  const onCopy = useCallback(async () => {
    setCopied(await copyText(report));
  }, [report]);

  return (
    <div
      role="alert"
      className="min-h-screen flex items-center justify-center px-6 py-10"
      style={{ background: '#1f1f1f', color: '#fff' }}
    >
      <div style={{ maxWidth: 720, width: '100%' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
          이 화면에서 문제가 생겼습니다 / Something went wrong on this page
        </h1>
        <p style={{ opacity: 0.8, marginBottom: 16, lineHeight: 1.6 }}>
          아래 «오류 내용 복사» 를 눌러 담당자에게 붙여 넣어 주세요. 다른 화면으로 이동하거나 새로고침하면 계속 쓸 수 있습니다.
          <br />
          Please press “Copy error details” and send it to us. You can go back or reload to keep working.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <button
            type="button"
            onClick={onCopy}
            style={{ background: '#cd7f61', color: '#fff', borderRadius: 8, padding: '10px 16px', fontWeight: 600 }}
          >
            {copied ? '복사됨 / Copied' : '오류 내용 복사 / Copy error details'}
          </button>
          <button
            type="button"
            onClick={() => window.history.back()}
            style={{ border: '1px solid #555', borderRadius: 8, padding: '10px 16px' }}
          >
            이전 화면 / Back
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ border: '1px solid #555', borderRadius: 8, padding: '10px 16px' }}
          >
            새로고침 / Reload
          </button>
        </div>
        <pre
          data-wg-notranslate="true"
          className="notranslate"
          translate="no"
          style={{
            background: '#111', border: '1px solid #333', borderRadius: 8, padding: 12, fontSize: 12,
            maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}
        >
          {report}
        </pre>
      </div>
    </div>
  );
}

class Boundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: '' };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ componentStack: info?.componentStack || '' });
    // 콘솔에도 남긴다 — 개발자 도구를 열 수 있는 사람은 그대로 볼 수 있다
    console.error('[RouteErrorBoundary]', error, info?.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorScreen
          error={this.state.error}
          componentStack={this.state.componentStack}
          location={this.props.location}
        />
      );
    }
    return this.props.children;
  }
}

/** 주소가 바뀌면 오류 상태를 버린다(key) — Router 안에서만 쓴다 */
export default function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <Boundary key={`${location.pathname}${location.search}`} location={location}>
      {children}
    </Boundary>
  );
}
